// HomePage.js - NGO-style, fully responsive, sharp edges (no border-radius)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../config/axiosConfig';
import './HomePage.css';
import TestimonialsSlider from '../components/TestimonialsSlider';
import {
  FaUsers, FaGraduationCap, FaHandsHelping, FaArrowRight, FaCut,
  FaCarrot, FaUtensils, FaTint, FaBook, FaPalette, FaIndustry, FaHome,
  FaGem, FaCoffee, FaShoePrints, FaShoppingBasket, FaBriefcase,
  FaChevronLeft, FaChevronRight, FaHeart, FaAward, FaClock, FaCheckCircle,
  FaEye, FaUserCheck, FaCertificate, FaChalkboardTeacher, FaTools, FaHandshake,
  FaTimes, FaLaptop, FaCode, FaRobot, FaDatabase, FaNetworkWired,
  FaShieldAlt, FaChartLine, FaRegBuilding, FaSpinner, FaInfoCircle,
  FaStar, FaUserTie, FaRegStar, FaCalendarAlt, FaMapMarkerAlt, FaUser,
  FaPlay, FaPause, FaBreadSlice, FaAppleAlt, FaLeaf, FaSeedling,
  FaTshirt, FaCouch, FaConciergeBell, FaFlask
} from 'react-icons/fa';
import RequestModal from '../components/RequestModal';
import { useTranslation } from '../contexts/TranslationContext';

const HomePage = ({ onDonateClick }) => {
  const { t, language } = useTranslation();

  const [departments, setDepartments] = useState([]);
  const [activityImages, setActivityImages] = useState([]);
  const [womenProfiles, setWomenProfiles] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [whyChooseUs, setWhyChooseUs] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [selectedWomanForRequest, setSelectedWomanForRequest] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const heroTimerRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsFilter, setEventsFilter] = useState('latest');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedActivityImage, setSelectedActivityImage] = useState(null);
  const [isHeroPlaying, setIsHeroPlaying] = useState(true);

  // ── HERO PRELOAD FIX: track what we've already preloaded + readiness flag
  const preloadedRef = useRef(new Set());
  const [heroReady, setHeroReady] = useState(false);

  // Department Women Modal State
  const [showDeptWomenModal, setShowDeptWomenModal] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptWomen, setDeptWomen] = useState([]);
  const [deptWomenLoading, setDeptWomenLoading] = useState(false);

  // ----- STATIC STATS -----
  const targetStats = [
    { id: 1, icon: 'FaUsers', value: '705+', label: 'Women Trained In TVET Program 2025', color: '#FF9800' },
    { id: 2, icon: 'FaHandsHelping', value: '350',  label: 'Ongoing Trained Women 2026', color: '#2196F3' },
    { id: 3, icon: 'FaUserCheck', value: '443',  label: 'Women Recruited By Company', color: '#E91E63' },
    { id: 4, icon: 'FaGraduationCap', value: '114', label: 'Women Trained In Computer And Literacy', color: '#4CAF50' },
    { id: 5, icon: 'FaUsers', value: '10',   label: 'Departments', color: '#9C27B0' }
  ];

  const extractArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.results && Array.isArray(data.results)) return data.results;
    if (data.rows && Array.isArray(data.rows)) return data.rows;
    if (data.items && Array.isArray(data.items)) return data.items;
    if (data.departments && Array.isArray(data.departments)) return data.departments;
    if (data.images && Array.isArray(data.images)) return data.images;
    if (data.women && Array.isArray(data.women)) return data.women;
    return [];
  };

  const fallbackDepartments = () => [
    { id: 1,  name: t('deptCoffeeShop') || 'Coffee Shop Operations',                    description: t('deptCoffeeShopDesc') || 'Professional barista training, coffee preparation, and cafe management', is_active: true },
    { id: 2,  name: t('deptHousekeeping') || 'Housekeeping Operations',                 description: t('deptHousekeepingDesc') || 'Professional cleaning, organization, and hospitality management', is_active: true },
    { id: 3,  name: t('deptCarpetYarn') || 'Carpet & Yarn Production',                  description: t('deptCarpetYarnDesc') || 'Hand-woven carpet making and yarn production using traditional and modern methods', is_active: true },
    { id: 4,  name: t('deptLeatherFootwear') || 'Leather Goods and Footwear Design and Production', description: t('deptLeatherFootwearDesc') || 'Leather goods production including bags, shoes, and accessories', is_active: true },
    { id: 5,  name: t('deptSewingGarment') || 'Sewing and Garment Production',          description: t('deptSewingGarmentDesc') || 'Professional sewing, tailoring, and garment production', is_active: true },
    { id: 6,  name: t('deptMilkProcessing') || 'Milk Processing',                       description: t('deptMilkProcessingDesc') || 'Milk and yogurt processing, packaging, and dairy product development', is_active: true },
    { id: 7,  name: t('deptBakeryPastry') || 'Bakery & Pastry Production',              description: t('deptBakeryPastryDesc') || 'Professional baking, pastry making, and confectionery skills', is_active: true },
    { id: 8,  name: t('deptNutritionalFood') || 'Nutritional Food Production',          description: t('deptNutritionalFoodDesc') || 'Production of nutritious food products and balanced meal preparation', is_active: true },
    { id: 9,  name: t('deptReception') || 'Reception Operations',                       description: t('deptReceptionDesc') || 'Professional receptionist, customer service, and office management', is_active: true },
    { id: 10, name: t('deptHorticulture') || 'Horticulture Production (Agriculture)',   description: t('deptHorticultureDesc') || 'Sustainable horticulture production, vegetable farming, and agricultural practices', is_active: true }
  ];

  const fallbackActivityImages = () => [
    { id: 1, image_url: '/graduation.png', description: 'Graduation ceremony', department_name: 'Training' },
    { id: 2, image_url: '/training.png', description: 'Skills training session', department_name: 'Training' },
    { id: 3, image_url: '/work.png', description: 'Women at work', department_name: 'Weaving' },
    { id: 4, image_url: '/sewing.png', description: 'Sewing workshop', department_name: 'Tailoring' },
    { id: 5, image_url: '/coffee.png', description: 'Coffee training', department_name: 'Coffee Shop' },
    { id: 6, image_url: '/leather.png', description: 'Leather crafting', department_name: 'Leather' }
  ];

  const fallbackWhyChooseUs = () => [
    { id: 1, title: 'Experienced Trainers', description: 'Learn from industry experts with years of experience', icon_name: 'FaChalkboardTeacher' },
    { id: 2, title: 'Hands-on Training', description: 'Practical skills you can use immediately', icon_name: 'FaTools' },
    { id: 3, title: 'Job Placement Support', description: 'We connect you with employers', icon_name: 'FaHandshake' },
    { id: 4, title: 'Flexible Schedules', description: 'Classes that fit your life', icon_name: 'FaClock' },
    { id: 5, title: 'Certified Programs', description: 'Recognized certificates', icon_name: 'FaCertificate' },
    { id: 6, title: 'Community Support', description: 'Join a supportive community of women', icon_name: 'FaUsers' }
  ];

  const fallbackHeroSlides = () => [
    {
      id: 1,
      title: 'Empowering Women, Transforming Communities',
      subtitle: 'Creating Sustainable Livelihoods',
      description: 'URUGO Women\'s Opportunity Center empowers women through skills training, economic empowerment, and creating sustainable livelihoods in Rwanda and beyond.',
      image_url: '/3.png',
      backgroundImage: '/3.png',
      media_type: 'image',
      button_text: 'Explore Programs',
      buttonText: 'Explore Programs',
      button_link: '#departments',
      buttonLink: '#departments',
      badge: 'Our Mission'
    },
    {
      id: 2,
      title: 'Skills for Life, Dignity for All',
      subtitle: 'Authentic Rwandan Craftsmanship',
      description: 'Discover handmade products crafted by skilled women artisans from across Rwanda — every purchase supports a family.',
      image_url: '/5.jpg',
      backgroundImage: '/5.jpg',
      media_type: 'image',
      button_text: 'Our Impact',
      buttonText: 'Our Impact',
      button_link: '#women',
      buttonLink: '#women',
      badge: 'Our Work'
    },
    {
      id: 3,
      title: 'Partner With Us',
      subtitle: 'Every Contribution Makes a Difference',
      description: 'Your support helps create sustainable livelihoods for women and their families in Rwanda.',
      image_url: '/7.jpeg',
      backgroundImage: '/7.jpeg',
      media_type: 'image',
      button_text: 'Hire Talent',
      buttonText: 'Hire Talent',
      button_link: '#departments',
      buttonLink: '#departments',
      badge: 'Get Involved'
    }
  ];

  const fallbackWomenProfiles = () => {
    const names = ['Marie Claire', 'Jeanne d\'Arc', 'Grace Uwase', 'Diane Mukamana', 'Claudine Uwimana', 'Chantal Ingabire', 'Beata Nyirahabimana', 'Ange Uwase'];
    const depts = ['Coffee Shop', 'Housekeeping', 'Carpet & Yarn', 'Leather', 'Sewing', 'Milk Processing', 'Bakery', 'Nutritional Food', 'Reception', 'Horticulture'];
    return Array(12).fill().map((_, i) => ({
      id: i + 1,
      name: names[i % names.length] + (i >= names.length ? ` ${Math.floor(i / names.length) + 1}` : ''),
      department_name: depts[i % depts.length],
      skills: `Expert in ${depts[i % depts.length]}`,
      experience_years: Math.floor(Math.random() * 8) + 1,
      is_available: true,
      employment_status: 'unemployed',
      has_certificate: Math.random() > 0.5,
      certificate_verified: Math.random() > 0.3,
      photo_url: `https://randomuser.me/api/portraits/women/${i + 10}.jpg`
    }));
  };

  const fetchDepartments = async () => {
    try {
      const res = await axios.get('https://hsn.urugowoc.org.rw/backend/api/departments');
      let data = extractArray(res.data);
      if (data.length === 0) throw new Error('No departments from API');
      const filtered = data.filter(d => {
        const n = (d.name || '').toLowerCase();
        return !(n.includes('it ') || n.includes('ai') || n.includes('i.t') || n.includes('technology'));
      });
      setDepartments(filtered.length > 0 ? filtered : fallbackDepartments());
    } catch (err) {
      console.error('❌ Departments API failed, using fallback:', err);
      setDepartments(fallbackDepartments());
    }
  };

  const fetchActivityImages = async () => {
    try {
      const res = await axios.get('https://hsn.urugowoc.org.rw/backend/api/activity-images', {
        params: { limit: 200 }
      });
      let data = extractArray(res.data);
      if (data.length === 0) throw new Error('No activity images');
      setActivityImages(data);
    } catch (err) {
      console.error('❌ Activity images API failed, using fallback:', err);
      setActivityImages(fallbackActivityImages());
    }
  };

  const fetchWomenProfiles = async () => {
    try {
      const res = await axios.get('https://hsn.urugowoc.org.rw/backend/api/women-profiles', {
        params: { include_certificates: true, limit: 500 }
      });
      let women = extractArray(res.data);
      if (women.length === 0) throw new Error('No women profiles');
      const processed = women
        .map(w => ({
          ...w,
          has_certificate: !!(w.certificate_url || w.certificate),
          certificate_verified: w.certificate_verified === true || w.certificate_verified === 1 || w.certificate_verified === 'true',
          is_available: w.is_available !== false,
          photo_url: w.photo_url || w.profile_image,
          department_name: w.department_name || w.department || 'Skilled Professional',
          skills: w.skills || `${w.department_name || 'Skilled'} professional`,
          experience_years: w.experience_years || 0,
          employment_status: w.employment_status || 'unemployed'
        }))
        .filter(w => w.employment_status === 'unemployed');
      setWomenProfiles(processed.length ? processed : fallbackWomenProfiles());
    } catch (err) {
      console.error('❌ Women profiles API failed, using fallback:', err);
      setWomenProfiles(fallbackWomenProfiles());
    }
  };

  const fetchDepartmentWomen = async (deptId) => {
    setDeptWomenLoading(true);
    try {
      const res = await axios.get(`https://hsn.urugowoc.org.rw/backend/api/department/${deptId}/women`);
      const data = Array.isArray(res.data) ? res.data : [];
      const onlyUnemployed = data.filter(w => {
        const status = (w.employment_status || 'unemployed').toLowerCase();
        return status === 'unemployed' || status === '' || status === null;
      });
      setDeptWomen(onlyUnemployed);
    } catch (error) {
      console.error('Error fetching department women:', error);
      setDeptWomen([]);
    } finally {
      setDeptWomenLoading(false);
    }
  };

  const fetchEvents = useCallback(async (filter) => {
    setEventsLoading(true);
    try {
      const res = await axios.get(`https://hsn.urugowoc.org.rw/backend/api/events?type=${filter}&limit=20`);
      const data = res.data;
      const eventsData = Array.isArray(data) ? data : [];
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      let filtered = eventsData;
      if (filter === 'latest') {
        filtered = eventsData.filter(e => {
          if (!e.event_date) return false;
          const eventDate = new Date(e.event_date);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate < now;
        });
      } else if (filter === 'upcoming') {
        filtered = eventsData.filter(e => {
          if (!e.event_date) return false;
          const eventDate = new Date(e.event_date);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate >= now;
        });
      }
      setEvents(filtered);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  // ── REPLACED: fetchHeroSlides with preload + heroReady flag
  const fetchHeroSlides = async () => {
    let slidesToUse;

    try {
      const res = await axios.get('https://hsn.urugowoc.org.rw/backend/api/hero-slides');
      let slides = extractArray(res.data);

      const validSlides = (slides || []).filter(
        s => s.title && (s.backgroundImage || s.image_url)
      );

      if (validSlides.length > 0) {
        slidesToUse = validSlides.map(s => ({
          ...s,
          backgroundImage: s.backgroundImage || s.image_url,
          buttonText: s.buttonText || s.button_text,
          buttonLink: s.buttonLink || s.button_link,
          badge: s.badge || ''
        }));
      } else {
        slidesToUse = fallbackHeroSlides();
      }
    } catch (err) {
      console.error('❌ Hero slides API failed, using fallback:', err.message);
      slidesToUse = fallbackHeroSlides();
    }

    // 1) Paint the slides structure immediately
    setHeroSlides(slidesToUse);
    setHeroReady(true);

    // 2) Preload every slide's image in parallel so slides 2/3 are ready
    slidesToUse.forEach(slide => {
      const url = slide.backgroundImage || slide.image_url;
      if (!url || preloadedRef.current.has(url)) return;
      preloadedRef.current.add(url);

      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      if (img.decode) {
        img.decode()
          .then(() => {
            // Nudge React so the <img> in the first slide repaints crisply
            setHeroSlides(prev => [...prev]);
          })
          .catch(() => {});
      }
    });
  };

  const fetchWhyChooseUs = async () => {
    try {
      const res = await axios.get('https://hsn.urugowoc.org.rw/backend/api/why-choose-us');
      let items = extractArray(res.data);
      if (items.length === 0) throw new Error('No why-choose-us items');
      setWhyChooseUs(items);
    } catch (err) {
      console.error('❌ Why choose us API failed, using fallback:', err);
      setWhyChooseUs(fallbackWhyChooseUs());
    }
  };

  const loadAllData = async () => {
    await fetchDepartments();
    await fetchActivityImages();
    await fetchWomenProfiles();
    await fetchHeroSlides();
    await fetchWhyChooseUs();
  };

  useEffect(() => {
    loadAllData();
  }, [language]);

  useEffect(() => {
    fetchEvents(eventsFilter);
  }, [eventsFilter, fetchEvents]);

  // SCROLL-REVEAL OBSERVER
  useEffect(() => {
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll(
        '.homepage-fade-up, .homepage-fade-left, .homepage-fade-right, .homepage-zoom-in, .homepage-stagger'
      );

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
      );

      elements.forEach(el => observer.observe(el));
    }, 120);

    return () => clearTimeout(timer);
  }, [departments, activityImages, womenProfiles, heroSlides, whyChooseUs, events]);

  // Hero slider
  const nextSlide = useCallback(() => {
    if (heroSlides.length) setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, [heroSlides.length]);

  const prevSlide = () => {
    if (heroSlides.length) setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const goToSlide = (index) => setCurrentSlide(index);

  useEffect(() => {
    if (heroSlides.length <= 1 || !isHeroPlaying) return;
    heroTimerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => {
      if (heroTimerRef.current) clearInterval(heroTimerRef.current);
    };
  }, [heroSlides.length, isHeroPlaying]);

  const resetHeroTimer = () => {
    if (heroTimerRef.current) clearInterval(heroTimerRef.current);
    if (heroSlides.length > 1 && isHeroPlaying) {
      heroTimerRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
    }
  };

  const toggleHeroPlay = () => {
    const newState = !isHeroPlaying;
    setIsHeroPlaying(newState);
    if (heroTimerRef.current) clearInterval(heroTimerRef.current);
    if (newState && heroSlides.length > 1) {
      heroTimerRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') prevSlide();
      else if (e.key === 'ArrowRight') nextSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide]);

  // Handlers
  const handleButtonClick = (link) => {
    if (!link) return;
    if (link.startsWith('#')) {
      const element = document.querySelector(link);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleRequestClick = (deptId) => {
    setSelectedDeptId(deptId);
    setSelectedWomanForRequest(null);
    setShowRequestModal(true);
  };

  const handleRequestWoman = (woman) => {
    if (!canRequestWoman(woman)) {
      alert(t('womanNotAvailable') || 'This woman is not currently available for hire');
      return;
    }
    setSelectedWomanForRequest(woman);
    setSelectedDeptId(null);
    setShowRequestModal(true);
  };

  const handleRequestSubmit = async (requestData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert(t('loginToRequest') || 'Please login to request women');
        return;
      }
      const requestPayload = {
        ...requestData,
        ...(selectedWomanForRequest && { requested_woman_id: selectedWomanForRequest.id })
      };
      await axios.post('https://hsn.urugowoc.org.rw/backend/api/employer/request', requestPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Request submitted successfully!');
      setShowRequestModal(false);
      setSelectedWomanForRequest(null);
      setSelectedDeptId(null);
      await fetchWomenProfiles();
    } catch (error) {
      console.error('Error submitting request:', error);
      alert(error.response?.data?.error || error.response?.data?.message || 'Failed to submit request');
    }
  };

  const handleViewCertificate = (woman) => {
    if (!woman.certificate_url && !woman.certificate) return;
    setCertificateLoading(true);
    const certUrl = woman.certificate_url || woman.certificate;
    const fullCertUrl = certUrl.startsWith('http') ? certUrl : `https://hsn.urugowoc.org.rw/${certUrl.startsWith('/') ? '' : '/'}${certUrl}`;
    window.open(fullCertUrl, '_blank');
    setCertificateLoading(false);
  };

  const canRequestWoman = (woman) => {
    if (!woman.is_available) return false;
    const status = woman.employment_status;
    if (status === 'employed' || status === 'self_employed' || status === 'working_for_others') {
      return false;
    }
    return true;
  };

  const handleDepartmentClick = (dept) => {
    setSelectedDept(dept);
    fetchDepartmentWomen(dept.id);
    setShowDeptWomenModal(true);
  };

  const getIconComponent = (iconName) => {
    const map = {
      FaUsers: <FaUsers />, FaGraduationCap: <FaGraduationCap />, FaHandsHelping: <FaHandsHelping />,
      FaHeart: <FaHeart />, FaCut: <FaCut />, FaCarrot: <FaCarrot />, FaUtensils: <FaUtensils />,
      FaTint: <FaTint />, FaBook: <FaBook />, FaPalette: <FaPalette />, FaIndustry: <FaIndustry />,
      FaHome: <FaHome />, FaGem: <FaGem />, FaCoffee: <FaCoffee />, FaShoePrints: <FaShoePrints />,
      FaShoppingBasket: <FaShoppingBasket />, FaBriefcase: <FaBriefcase />, FaLaptop: <FaLaptop />,
      FaCode: <FaCode />, FaRobot: <FaRobot />, FaDatabase: <FaDatabase />, FaNetworkWired: <FaNetworkWired />,
      FaShieldAlt: <FaShieldAlt />, FaChartLine: <FaChartLine />, FaRegBuilding: <FaRegBuilding />,
      FaUserTie: <FaUserTie />, FaStar: <FaStar />, FaRegStar: <FaRegStar />,
      FaChalkboardTeacher: <FaChalkboardTeacher />, FaTools: <FaTools />, FaHandshake: <FaHandshake />,
      FaClock: <FaClock />, FaCertificate: <FaCertificate />, FaCheckCircle: <FaCheckCircle />,
      FaBreadSlice: <FaBreadSlice />, FaAppleAlt: <FaAppleAlt />, FaLeaf: <FaLeaf />,
      FaSeedling: <FaSeedling />, FaTshirt: <FaTshirt />, FaCouch: <FaCouch />,
      FaConciergeBell: <FaConciergeBell />, FaFlask: <FaFlask />
    };
    return map[iconName] || <FaHeart />;
  };

  const getDepartmentImage = (dept) => {
    if (dept.image_url) return dept.image_url;
    const name = dept.name?.toLowerCase() || '';
    if (name.includes('coffee')) return '/coffee.png';
    if (name.includes('housekeep')) return '/housekeeping.png';
    if (name.includes('carpet') || name.includes('yarn')) return '/carpet2.png';
    if (name.includes('leather') || name.includes('shoe') || name.includes('footwear')) return '/leather.png';
    if (name.includes('sewing') || name.includes('garment') || name.includes('tailor')) return '/sewing.png';
    if (name.includes('milk')) return '/milk.jpeg';
    if (name.includes('bakery') || name.includes('pastry')) return '/bakery.png';
    if (name.includes('nutritional') || name.includes('food')) return '/Food.png';
    if (name.includes('reception')) return '/receiption.png';
    if (name.includes('horticulture') || name.includes('agriculture') || name.includes('farm')) return '/w2.png';
    return '/Agriculture.jpeg';
  };

  const getDepartmentColor = (deptName) => {
    const name = deptName?.toLowerCase() || '';
    if (name.includes('coffee')) return '#E67E22';
    if (name.includes('housekeeping')) return '#27AE60';
    if (name.includes('carpet') || name.includes('yarn')) return '#9B59B6';
    if (name.includes('leather') || name.includes('footwear')) return '#D35400';
    if (name.includes('sewing') || name.includes('garment') || name.includes('tailor')) return '#E91E63';
    if (name.includes('milk')) return '#3498DB';
    if (name.includes('bakery') || name.includes('pastry')) return '#F39C12';
    if (name.includes('nutritional') || name.includes('food')) return '#F44336';
    if (name.includes('reception')) return '#3498DB';
    if (name.includes('horticulture') || name.includes('agriculture') || name.includes('farm')) return '#4CAF50';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash |= 0;
    }
    return `hsl(${Math.abs(hash % 360)}, 70%, 45%)`;
  };

  const CertificateStatusBadge = ({ woman }) => {
    if (!woman.has_certificate) return null;
    return (
      <div className="homepage-certificate-badge">
        <FaCertificate className={woman.certificate_verified ? 'verified' : 'unverified'} />
        <span className="homepage-certificate-tooltip">
          {woman.certificate_verified
            ? t('certificateVerified') || 'Verified Certificate'
            : t('certificateUnverified') || 'Certificate Pending Verification'}
        </span>
      </div>
    );
  };

  const WomanCard = ({ woman }) => {
    const deptColor = getDepartmentColor(woman.department_name);
    const showRequest = canRequestWoman(woman);
    return (
      <div className="homepage-woman-card" style={{ '--dept-color': deptColor }}>
        <div className="homepage-woman-info">
          <h3>{woman.name || 'Trained Woman'}</h3>
          <p className="homepage-woman-department">
            <FaGraduationCap style={{ color: deptColor }} /> {woman.department_name}
            <CertificateStatusBadge woman={woman} />
          </p>
          <p className="homepage-woman-employment-status">
            {woman.employment_status ? t(woman.employment_status) || woman.employment_status : 'Status unknown'}
          </p>
          <div className="homepage-woman-details">
            {woman.experience_years > 0 && (
              <span className="homepage-woman-experience">
                <FaClock /> {woman.experience_years} {t('yearsExperience') || 'years exp.'}
              </span>
            )}
            {woman.has_certificate && (
              <span className="homepage-woman-certificate-info">
                <FaCertificate /> {woman.certificate_verified ? t('certified') || 'Certified' : t('certificatePending') || 'Pending'}
              </span>
            )}
          </div>
          <div className="homepage-woman-actions">
            {woman.has_certificate && woman.certificate_verified && (
              <button className="homepage-woman-action-btn certificate-btn" onClick={() => handleViewCertificate(woman)} disabled={certificateLoading}>
                <FaEye /> {t('viewCertificate') || 'Certificate'}
              </button>
            )}
            {showRequest && (
              <button className="homepage-woman-action-btn request-btn" onClick={() => handleRequestWoman(woman)}>
                <FaUserCheck /> {t('request') || 'Request'}
              </button>
            )}
            {!showRequest && woman.is_available && (
              <div className="homepage-woman-not-available">{t('notAvailableForHire') || 'Not available for hire'}</div>
            )}
            {!woman.is_available && <div className="homepage-woman-not-available">{t('currentlyEmployed') || 'Employed'}</div>}
          </div>
        </div>
      </div>
    );
  };

  const openActivityModal = (image) => {
    setSelectedActivityImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeActivityModal = () => {
    setSelectedActivityImage(null);
    document.body.style.overflow = 'auto';
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const marqueeDuration = Math.max(40, womenProfiles.length * 5);

  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="homepage-hero" id="home">
        <div className="homepage-hero-wrapper">
          <div
            className="homepage-hero-slider"
            onTouchStart={(e) => setTouchStart(e.targetTouches[0].clientX)}
            onTouchMove={(e) => setTouchEnd(e.targetTouches[0].clientX)}
            onTouchEnd={() => {
              if (touchStart && touchEnd) {
                const dist = touchStart - touchEnd;
                if (dist > 50) { nextSlide(); resetHeroTimer(); }
                if (dist < -50) { prevSlide(); resetHeroTimer(); }
                setTouchStart(null);
                setTouchEnd(null);
              }
            }}
          >
            {/* ── HERO SKELETON — paints instantly while first slide loads */}
            {!heroReady && (
              <div className="homepage-hero-skeleton" aria-hidden="true" />
            )}

            <div className="homepage-hero-slides">
              {heroSlides.map((slide, index) => {
                const isActive = index === currentSlide;
                return (
                  <div
                    key={slide.id || index}
                    className={`homepage-hero-slide ${isActive ? 'active' : ''}`}
                  >
                    <div className="homepage-hero-slide-background">
                      {slide.media_type === 'video' && slide.video_url ? (
                        <video
                          className="homepage-hero-slide-video"
                          src={slide.video_url}
                          poster={slide.image_url || '/images/placeholder.jpg'}
                          muted
                          loop
                          playsInline
                          autoPlay={isActive && isHeroPlaying}
                        />
                      ) : (
                        <img
                          className="homepage-hero-slide-image"
                          src={slide.backgroundImage || slide.image_url || '/urugo1.png'}
                          alt={slide.title || 'Hero'}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          fetchpriority={index === 0 ? 'high' : 'auto'}
                          decoding="async"
                          draggable={false}
                        />
                      )}
                      <div className="homepage-hero-overlay" />
                    </div>

                    <div className="homepage-hero-content">
                      <div className="homepage-container">
                        <div className="homepage-hero-text homepage-fade-up">
                          {slide.badge && (
                            <span className="homepage-hero-badge">{slide.badge}</span>
                          )}
                          <h1 className="homepage-hero-title">{slide.title}</h1>
                          {slide.subtitle && (
                            <p className="homepage-hero-subtitle">{slide.subtitle}</p>
                          )}
                          {slide.description && (
                            <p className="homepage-hero-description">{slide.description}</p>
                          )}
                          <div className="homepage-hero-buttons">
                            {slide.buttonLink && (
                              <button
                                className="homepage-hero-btn-primary"
                                onClick={() => handleButtonClick(slide.buttonLink)}
                              >
                                {slide.buttonText || 'Learn More'} <FaArrowRight />
                              </button>
                            )}
                            <button
                              className="homepage-hero-btn-outline"
                              onClick={() => handleButtonClick('#departments')}
                            >
                              Hire Talent
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {heroSlides.length > 1 && (
              <div className="homepage-hero-progress-container">
                <div
                  className={`homepage-hero-progress-bar ${isHeroPlaying ? 'active' : 'paused'}`}
                  key={currentSlide}
                />
              </div>
            )}

            {heroSlides.length > 1 && (
              <>
                <button
                  className="homepage-hero-arrow homepage-hero-arrow-prev"
                  onClick={() => { prevSlide(); resetHeroTimer(); }}
                  aria-label="Previous slide"
                >
                  <FaChevronLeft />
                </button>
                <button
                  className="homepage-hero-arrow homepage-hero-arrow-next"
                  onClick={() => { nextSlide(); resetHeroTimer(); }}
                  aria-label="Next slide"
                >
                  <FaChevronRight />
                </button>
              </>
            )}

            {heroSlides.length > 1 && (
              <div className="homepage-hero-dots">
                {heroSlides.map((_, idx) => (
                  <button
                    key={idx}
                    className={`homepage-hero-dot ${idx === currentSlide ? 'active' : ''}`}
                    onClick={() => { goToSlide(idx); resetHeroTimer(); }}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}

            {heroSlides.length > 0 && (
              <div className="homepage-hero-counter">
                <span className="homepage-hero-counter-current">
                  {String(currentSlide + 1).padStart(2, '0')}
                </span>
                <span className="homepage-hero-counter-separator">/</span>
                <span className="homepage-hero-counter-total">
                  {String(heroSlides.length).padStart(2, '0')}
                </span>
              </div>
            )}

            {heroSlides.length > 1 && (
              <button
                className="homepage-hero-playpause"
                onClick={toggleHeroPlay}
                aria-label={isHeroPlaying ? 'Pause slideshow' : 'Play slideshow'}
                title={isHeroPlaying ? 'Pause' : 'Play'}
              >
                {isHeroPlaying ? <FaPause /> : <FaPlay />}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* NGO-style Intro band */}
      <section className="homepage-intro homepage-fade-up">
        <div className="homepage-container">
          <div className="homepage-intro-inner homepage-stagger">
            <div className="homepage-intro-text">
              <span className="homepage-intro-tag">Who We Are</span>
              <h2>
                URUGO Women's Opportunity Center is
                committed to empowering women through practical skills,
                dignified work, and sustainable livelihoods.
              </h2>
            </div>
            <div className="homepage-intro-meta">
              <div className="homepage-intro-meta-item">
                <FaUsers />
                <div>
                  <strong>705+</strong>
                  <span>Women Trained In TVET Program 2025

</span>
                </div>
              </div>
              <div className="homepage-intro-meta-item">
                <FaHandsHelping />
                <div>
                  <strong>10</strong>
                  <span>Skill departments</span>
                </div>
              </div>
              <div className="homepage-intro-meta-item">
                <FaAward />
                <div>
                  <strong>Certified</strong>
                  <span>TVET programs</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="homepage-stats-section homepage-fade-up">
        <div className="homepage-container">
          <div className="homepage-stats-grid homepage-stagger">
            {targetStats.map((stat) => (
              <div key={stat.id} className="homepage-stat-card">
                <div className="homepage-stat-icon" style={{ color: stat.color }}>
                  {getIconComponent(stat.icon)}
                </div>
                <div className="homepage-stat-content">
                  <h3>{stat.value}</h3>
                  <p>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Departments */}
      <section id="departments" className="homepage-departments homepage-fade-up">
        <div className="homepage-container">
          <div className="homepage-section-header">
            <span className="homepage-section-tag">{t('ourPrograms') || 'Our Programs'}</span>
            <h2 className="homepage-section-title">{t('ourTrainingDepartments') || "Our Training Departments"}</h2>
            <p className="homepage-section-subtitle">
              {t('chooseSpecializedPrograms')?.replace('{count}', departments.length) ||
                `Choose from our ${departments.length} specialized training programs designed for success`}
            </p>
          </div>
          {departments.length > 0 ? (
            <div className="homepage-departments-grid homepage-departments-grid-5 homepage-stagger">
              {departments.map((dept, idx) => {
                const deptColor = getDepartmentColor(dept.name);
                return (
                  <div
                    key={dept.id}
                    className="homepage-department-card"
                    style={{ '--dept-color': deptColor, animationDelay: `${idx * 0.08}s` }}
                    onClick={() => handleDepartmentClick(dept)}
                  >
                    <div className="homepage-department-image">
                      <img
                        src={getDepartmentImage(dept)}
                        alt={dept.name}
                        loading="lazy"
                        onError={(e) => (e.target.src = '/graduation.png')}
                      />
                    </div>
                    <div className="homepage-department-caption">
                      <h3>{dept.name}</h3>
                      <button
                        className="homepage-department-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestClick(dept.id);
                        }}
                      >
                        {t('requestTrainedWomen') || "Request Trained Women"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="homepage-no-data"><p>{t('noDepartmentsAvailable') || 'No departments available at the moment.'}</p></div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="homepage-why-choose-section homepage-fade-up">
        <div className="homepage-container">
          <div className="homepage-section-header">
            <span className="homepage-section-tag">{t('whyUs') || 'Why Us'}</span>
            <h2 className="homepage-section-title">{t('whyChooseUs') || 'Why Choose Us'}</h2>
          </div>
          <div className="homepage-why-choose-features homepage-stagger">
            {(whyChooseUs.length ? whyChooseUs.slice(0, 6) : fallbackWhyChooseUs()).map((item, i) => (
              <div key={item.id || i} className="homepage-why-choose-item">
                <div className="homepage-why-choose-icon">{getIconComponent(item.icon_name)}</div>
                <div className="homepage-why-choose-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY — Women in Action */}
      <section className="homepage-gallery homepage-fade-up">
        <div className="homepage-container">
          <div className="homepage-section-header">
            <span className="homepage-section-tag">{t('gallery') || 'Gallery'}</span>
            <h2 className="homepage-section-title">{t('ourWomenInAction') || "Our Women in Action"}</h2>
            <p className="homepage-section-subtitle">{t('witnessTransformation') || "Witness the transformation through their work"}</p>
          </div>
          {activityImages.length > 0 ? (
            <div className="homepage-gallery-grid homepage-gallery-grid-6 homepage-stagger">
              {activityImages.map((img, idx) => (
                <div
                  key={img.id || idx}
                  className="homepage-gallery-item"
                  onClick={() => openActivityModal(img)}
                >
                  <div className="homepage-gallery-image">
                    <img
                      src={img.image_url || '/graduation.png'}
                      alt={img.description || 'Activity'}
                      loading="lazy"
                      onError={(e) => (e.target.src = '/graduation.png')}
                    />
                  </div>
                  <div className="homepage-gallery-overlay">
                    <span className="homepage-gallery-tag">{img.department_name || t('training') || 'Training'}</span>
                    <p>{img.description || t('womenEmpowermentActivity') || 'Women empowerment activity'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="homepage-no-data"><p>{t('noGalleryImages') || 'No gallery images available at the moment.'}</p></div>
          )}
        </div>
      </section>

      {/* WOMEN — Marquee */}
      <section id="women" className="homepage-women homepage-fade-up">
        <div className="homepage-container">
          <div className="homepage-section-header">
            <span className="homepage-section-tag">{t('ourTalent') || 'Our Talent'}</span>
            <h2 className="homepage-section-title">{t('meetTalentedWomen') || "Meet Our Talented Women"}</h2>
            <p className="homepage-section-subtitle">{t('skilledProfessionals') || "Skilled professionals ready to make a difference"}</p>
            <div className="homepage-certificate-legend">
              <div className="legend-item"><FaCertificate className="verified" /><span>{t('verifiedCertificate') || 'Verified Certificate'}</span></div>
              <div className="legend-item"><FaCertificate className="unverified" /><span>{t('pendingVerification') || 'Pending Verification'}</span></div>
              <div className="legend-item"><FaUserCheck /><span>{t('availableForHire') || 'Available for Hire'}</span></div>
            </div>
          </div>
          {womenProfiles.length > 0 ? (
            <div
              className="homepage-women-marquee"
              style={{ '--marquee-duration': `${marqueeDuration}s` }}
            >
              <div className="homepage-women-marquee-track">
                {womenProfiles.map((woman, idx) => (
                  <WomanCard key={`a-${woman.id || idx}`} woman={woman} />
                ))}
                {womenProfiles.map((woman, idx) => (
                  <WomanCard key={`b-${woman.id || idx}`} woman={woman} aria-hidden="true" />
                ))}
              </div>
            </div>
          ) : (
            <div className="homepage-no-data"><p>{t('noWomenProfiles') || 'No women profiles available at the moment.'}</p></div>
          )}
        </div>
      </section>

      {/* Events */}
      <section className="homepage-events homepage-fade-up">
        <div className="homepage-container">
          <div className="homepage-section-header">
            <span className="homepage-section-tag">{t('updates') || 'Updates'}</span>
            <h2 className="homepage-section-title">{t('events') || 'Events & Updates'}</h2>
            <p className="homepage-section-subtitle">
              {t('stayUpdated') || 'Stay informed about our latest activities and upcoming events'}
            </p>
          </div>

          <div className="homepage-events-filter">
            <button
              className={`homepage-events-filter-btn ${eventsFilter === 'latest' ? 'active' : ''}`}
              onClick={() => setEventsFilter('latest')}
            >
              {t('latestUpdates') || 'Latest Updates'}
            </button>
            <button
              className={`homepage-events-filter-btn ${eventsFilter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setEventsFilter('upcoming')}
            >
              {t('upcomingEvents') || 'Upcoming Events'}
            </button>
          </div>

          {events.length === 0 ? (
            <div className="homepage-events-fallback">
              <FaInfoCircle className="homepage-events-fallback-icon" />
              <p>
                {t('noEventsLine') ||
                  'Urugo Women Opportunity Center (Urugo WOC) empowers women through skills training and job placement — stay tuned for our next event.'}
              </p>
            </div>
          ) : (
            <div className="homepage-events-scroll-wrapper">
              <div className="homepage-events-scroll-track">
                {[...events, ...events, ...events].map((event, index) => (
                  <div
                    key={`${event.id}-${index}`}
                    className="homepage-event-card"
                    onClick={() => openEventModal(event)}
                  >
                    <div className="homepage-event-image">
                      <img
                        src={event.image_url || '/default-event.jpg'}
                        alt={event.title}
                        loading="lazy"
                        onError={(e) => (e.target.src = '/default-event.jpg')}
                      />
                      {event.event_date && (
                        <div className="homepage-event-date-badge">
                          <FaCalendarAlt />
                          <span>{new Date(event.event_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    <div className="homepage-event-info">
                      <h3>{event.title}</h3>
                      <p>{event.description.length > 80 ? event.description.substring(0, 80) + '...' : event.description}</p>
                      <span className="homepage-event-readmore">{t('readMore') || 'Click to read more'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <TestimonialsSlider />

      {/* NGO-style Quote band (no mission/vision) */}
      <section className="homepage-quote homepage-fade-up">
        <div className="homepage-container">
          <blockquote className="homepage-quote-inner">
            <p>“When you empower a woman, you empower a family, a community, and a generation.”</p>
            <cite>— URUGO Women's Opportunity Center</cite>
          </blockquote>
        </div>
      </section>

      {/* CTA */}
      <section className="homepage-cta homepage-fade-up">
        <div className="homepage-container">
          <div className="homepage-cta-content">
            <h2>{t('empowerMoreWomen') || "Empower More Women Today"}</h2>
            <p>{t('supportProvidesHope') || "Your support provides training, opportunities, and hope. Join us in creating lasting change."}</p>
            <div className="homepage-cta-buttons">
              <button className="homepage-cta-button homepage-cta-secondary" onClick={() => handleButtonClick('#departments')}>
                <FaBriefcase /> {t('hireTalent') || "Hire Talent"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {showRequestModal && (
        <RequestModal
          departmentId={selectedDeptId}
          selectedWoman={selectedWomanForRequest}
          onClose={() => {
            setShowRequestModal(false);
            setSelectedWomanForRequest(null);
            setSelectedDeptId(null);
          }}
          onSubmit={handleRequestSubmit}
        />
      )}

      {/* Activity Modal */}
      {selectedActivityImage && (
        <div className="homepage-activity-modal-overlay" onClick={closeActivityModal}>
          <div className="homepage-activity-modal" onClick={(e) => e.stopPropagation()}>
            <button className="homepage-activity-modal-close" onClick={closeActivityModal}>
              <FaTimes />
            </button>
            <div className="homepage-activity-modal-content">
              <div className="homepage-activity-modal-image">
                <img
                  src={selectedActivityImage.image_url || '/graduation.png'}
                  alt={selectedActivityImage.description || 'Activity'}
                  onError={(e) => (e.target.src = '/graduation.png')}
                />
              </div>
              <div className="homepage-activity-modal-caption">
                <h3>{selectedActivityImage.description || t('womenEmpowermentActivity') || 'Women empowerment activity'}</h3>
                {selectedActivityImage.department_name && (
                  <span className="homepage-activity-modal-dept">
                    {selectedActivityImage.department_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Department Women Modal */}
      {showDeptWomenModal && selectedDept && (
        <div className="homepage-event-modal-overlay" onClick={() => setShowDeptWomenModal(false)}>
          <div className="homepage-event-modal" onClick={(e) => e.stopPropagation()}>
            <button className="homepage-event-modal-close" onClick={() => setShowDeptWomenModal(false)}>
              <FaTimes />
            </button>
            <h2 style={{ marginBottom: '8px' }}>{selectedDept.name} – {t('trainedWomen') || 'Trained Women'}</h2>
            <p style={{ color: '#718096', marginBottom: '20px' }}>
              {deptWomenLoading ? t('loading') || 'Loading...' : `${deptWomen.length} ${t('unemployedWomenFound') || 'unemployed women found'}`}
            </p>
            {deptWomenLoading ? (
              <div style={{ textAlign: 'center', padding: '30px' }}>
                <FaSpinner className="homepage-spinning" size={30} />
              </div>
            ) : deptWomen.length === 0 ? (
              <div className="homepage-no-data">
                <p>{t('noUnemployedWomenInDepartment') || 'No unemployed women found in this department.'}</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
                {deptWomen.map((woman) => {
                  const showRequest = canRequestWoman(woman);
                  return (
                    <div key={woman.id} className="homepage-woman-card" style={{ padding: '16px', border: '1px solid #e2e8f0' }}>
                      <h4 style={{ margin: '0 0 4px 0', color: 'var(--primary-green)' }}>{woman.name}</h4>
                      <p style={{ fontSize: '0.85rem', margin: '4px 0', color: '#4a5568' }}>
                        {woman.employment_status ? t(woman.employment_status) || woman.employment_status : 'Status unknown'}
                      </p>
                      <div className="homepage-woman-actions" style={{ marginTop: '10px' }}>
                        {showRequest && (
                          <button className="homepage-woman-action-btn request-btn" onClick={() => handleRequestWoman(woman)}>
                            <FaUserCheck /> {t('request') || 'Request'}
                          </button>
                        )}
                        {!showRequest && woman.is_available && (
                          <div className="homepage-woman-not-available">{t('notAvailableForHire') || 'Not available for hire'}</div>
                        )}
                        {!woman.is_available && <div className="homepage-woman-not-available">{t('currentlyEmployed') || 'Employed'}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button className="homepage-cta-button homepage-cta-secondary" style={{ background: 'var(--primary-orange)', color: 'white' }} onClick={() => setShowDeptWomenModal(false)}>
                {t('close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && selectedEvent && (
        <div className="homepage-event-modal-overlay" onClick={closeEventModal}>
          <div className="homepage-event-modal" onClick={(e) => e.stopPropagation()}>
            <button className="homepage-event-modal-close" onClick={closeEventModal}>
              <FaTimes />
            </button>
            <div className="homepage-event-modal-content">
              {selectedEvent.image_url && (
                <div className="homepage-event-modal-image">
                  <img
                    src={selectedEvent.image_url}
                    alt={selectedEvent.title}
                    onError={(e) => (e.target.src = '/default-event.jpg')}
                  />
                </div>
              )}
              <h2>{selectedEvent.title}</h2>
              <div className="homepage-event-meta">
                {selectedEvent.event_date && (
                  <span><FaCalendarAlt /> {new Date(selectedEvent.event_date).toLocaleDateString()}</span>
                )}
                {selectedEvent.location && (
                  <span><FaMapMarkerAlt /> {selectedEvent.location}</span>
                )}
                {selectedEvent.organizer && (
                  <span><FaUser /> {selectedEvent.organizer}</span>
                )}
              </div>
              <div className="homepage-event-modal-description">
                {selectedEvent.description.split('\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;